import EdButton from '@Components/UI/Buttons/EdButton/EdButton';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import { FlexLayout, Spacer } from '@Styled/utilities';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { renderFormulasInHtml } from '@RichTextEditor';
import { testModelsGetSingle } from '../../../../Slice/TestModel/TestModel.slice';
import { TestQuestion } from '../../../../Types/TestQuestion';

/**
 * Read-only view of a model's questions.
 *
 * The editor is the only other way to see what a model contains, and it is
 * disabled while the test is active — so an active test's models could not be
 * inspected at all. This view has no form and no mutations.
 */
const PreviewTestModel = () => {
	const dispatch = useAppThunkDispatch();
	const history = useHistory();
	const { id } = useParams<{ id: string }>();
	// Explicit path rather than goBack(): this URL is a plain deep link, so a
	// refreshed or pasted preview has no history to go back to.
	const parentPath = history.location.pathname.replace(/\/[^/]+\/preview\/?$/, '');
	const { currentEntity, loaders } = useTypedSelector(
		(state) => state.TestModels
	);

	useEffect(() => {
		if (id) dispatch(testModelsGetSingle({ id: Number(id) }));
	}, [dispatch, id]);

	// currentEntity is slice-global and starts as {} (always truthy), and the
	// loader still reads 'fulfilled' from whichever model was fetched last —
	// so without the id check the first paint renders the PREVIOUS model's
	// questions and answer key under this URL.
	const loaded =
		loaders.getSingle === 'fulfilled' && String(currentEntity?.id) === String(id);

	if (loaders.getSingle === 'rejected' && !loaded) {
		return <ModelMeta>Couldn't load this model. Please try again.</ModelMeta>;
	}
	if (!loaded) {
		return <div>loading...</div>;
	}

	const questions = (currentEntity.questions ?? []) as Array<TestQuestion>;

	return (
		<div>
			<FlexLayout alignItems="center" justifyContent="space-between">
				<div>
					<ModelName>{currentEntity.name}</ModelName>
					<Spacer mb="0.5rem" />
					<ModelMeta>
						Questions: <strong>{questions.length}</strong>
						<Spacer mx="0.75rem" />
						{/* This model's own total. The test's overall score is what
						    students are graded against, and the two only agree once
						    every model matches — which activation enforces. */}
						Model Score: <strong>{currentEntity.questions_sum}</strong>
					</ModelMeta>
				</div>
				<EdButton
					onClick={() => history.push(parentPath)}
					variant="outlined"
					edcolor="dodgerBlue"
					startIcon={<EdIcon>arrow_back</EdIcon>}
				>
					Back
				</EdButton>
			</FlexLayout>
			<Spacer mb="1.5rem" />
			{questions.length === 0 && <ModelMeta>This model has no questions.</ModelMeta>}
			{questions.map((question, index) => (
				<QuestionCard key={question.id}>
					<QuestionHeader>
						<span>Q{index + 1}.</span>
						<FlexLayout alignItems="center">
							<QuestionType>{question.type}</QuestionType>
							<Spacer mx="0.5rem" />
							<QuestionWeight>{question.weight} pts</QuestionWeight>
						</FlexLayout>
					</QuestionHeader>
					<Spacer mb="0.75rem" />
					{/* Same pipeline every other question renderer uses — raw content
					    leaves formulas as unrendered markup. */}
					<QuestionContent
						dangerouslySetInnerHTML={{
							__html: renderFormulasInHtml(question.content ?? ''),
						}}
					/>
					{question.multipleChoiceOptions?.length > 0 && (
						<>
							<Spacer mb="0.75rem" />
							{question.multipleChoiceOptions.map((option, optionIndex) => (
								<Choice
									key={option.id ?? `${option.choice}-${optionIndex}`}
									correct={option.is_correct}
								>
									<EdIcon mIconType="Outlined">
										{option.is_correct ? 'check_circle' : 'radio_button_unchecked'}
									</EdIcon>
									<Spacer mx="0.5rem" />
									<span>{option.choice}</span>
								</Choice>
							))}
						</>
					)}
					{question.orderingOptions?.length > 0 && (
						<>
							<Spacer mb="0.75rem" />
							{question.orderingOptions.map((option, optionIndex) => (
								<Choice key={`ordering-${optionIndex}`} correct>
									<OrderBadge>{option.order ?? optionIndex + 1}</OrderBadge>
									<Spacer mx="0.5rem" />
									<span>{option.option}</span>
								</Choice>
							))}
						</>
					)}
					{question.fillInTheGapOptions?.length > 0 && (
						<>
							<Spacer mb="0.75rem" />
							{question.fillInTheGapOptions.map((gap, gapIndex) => (
								<Choice key={`gap-${gap.id ?? gapIndex}`} correct>
									<OrderBadge>{gap.gap ?? gapIndex + 1}</OrderBadge>
									<Spacer mx="0.5rem" />
									<span>{gap.correct}</span>
								</Choice>
							))}
						</>
					)}
					{/* An essay has no answer key to show — saying nothing would read
					    as a misconfigured question, and the generic message below
					    would send the admin looking for something that cannot exist. */}
					{question.type === 'essay' && (
						<ModelMeta>Graded by hand — this question has no answer key.</ModelMeta>
					)}
					{/* No renderer for this type yet — say so rather than showing a
					    bare stem that reads as "no answers configured". */}
					{question.type !== 'essay' &&
						!question.multipleChoiceOptions?.length &&
						!question.orderingOptions?.length &&
						!question.fillInTheGapOptions?.length && (
							<ModelMeta>
								Answers for this question type are not shown here — open the
								question to review them.
							</ModelMeta>
						)}
				</QuestionCard>
			))}
		</div>
	);
};

export default PreviewTestModel;

const ModelName = styled.div`
	font-size: 1.125rem;
	font-weight: bold;
`;
const ModelMeta = styled.div`
	font-size: 0.875rem;
	color: rgba(0, 0, 0, 0.6);
	display: flex;
	align-items: center;
`;
const QuestionCard = styled.div`
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	padding: 1rem;
	margin-bottom: 0.75rem;
`;
const QuestionHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-weight: 600;
`;
const QuestionType = styled.span`
	text-transform: uppercase;
	font-size: 0.75rem;
	color: rgba(0, 0, 0, 0.6);
`;
const QuestionWeight = styled.span`
	font-size: 0.75rem;
	color: rgba(0, 0, 0, 0.6);
`;
const QuestionContent = styled.div`
	max-width: 100%;
	img {
		max-width: 100%;
		height: auto;
	}
`;
const OrderBadge = styled.span`
	min-width: 1.25rem;
	text-align: center;
	font-size: 0.75rem;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.6);
`;
const Choice = styled.div<{ correct?: boolean }>`
	display: flex;
	align-items: center;
	padding: 0.375rem 0.5rem;
	border-radius: 6px;
	font-size: 0.875rem;
	background: ${({ correct }) => (correct ? 'rgba(0, 214, 107, 0.1)' : 'transparent')};
	color: ${({ correct }) => (correct ? '#00713a' : 'inherit')};
`;
